import { useMemo } from 'react'

import _ from 'lodash'

import { useDigitalProductContext } from '@/shared/contexts/DigitalProductContext'
import { useRecords } from '@/shared/hooks'
import { useUserProgress } from '@/shared/hooks/useUserProgress'

export type MedalItem = {
  id: number
  name: string
  imageUrl: string | null
  unlocked: boolean
  /**
   * Origem da conquista. `Medal` exige jornada **ou** missão — a validação da
   * api só recusa quando as duas faltam, então as duas preenchidas é estado
   * válido. Os ids já desciam no payload (`MedalSerializer` os tem entre os
   * `attributes`) e eram descartados aqui; sem eles a home só sabia dizer
   * "Complete jornadas", inclusive em evento de medalhas por missão.
   */
  journeyId: number | null
  missionId: number | null
}

export const useMedalsCollection = () => {
  const {
    config: { apiBasePath }
  } = useDigitalProductContext()

  const { userProgress } = useUserProgress()
  const medalIds: number[] = _.get(userProgress, 'medal_ids', [])

  const { records: medals, isLoading } = useRecords({
    r: `${apiBasePath}/medals`
  }) as { records: any[]; isLoading: boolean }

  return useMemo(() => {
    const unlockedSet = new Set(medalIds)

    const items: MedalItem[] = (medals ?? []).map((medal: any) => ({
      id: medal.id,
      name: medal.name,
      imageUrl: _.get(medal, 'media.file.url', null),
      unlocked: unlockedSet.has(medal.id),
      journeyId: medal.journey_id ?? null,
      missionId: medal.mission_id ?? null
    }))

    // Desbloqueadas primeiro — coleção lê como conquista, não como pendência.
    const sorted = [...items].sort(
      (a, b) => Number(b.unlocked) - Number(a.unlocked)
    )

    const unlockedCount = items.filter(item => item.unlocked).length
    const total = items.length
    const nextMedal = sorted.find(item => !item.unlocked) ?? null

    return {
      medals: sorted,
      unlockedCount,
      total,
      percent: total > 0 ? Math.round((unlockedCount / total) * 100) : 0,
      nextMedal,
      isEmpty: total === 0,
      isLoading
    }
  }, [medals, medalIds, isLoading])
}
